import { Controller } from "@hotwired/stimulus";

// Handles server-side sorting and filtering for a data table via a Turbo Frame.
// Sorting and filter changes navigate the frame to a new URL with the relevant
// query parameters, leaving any other params (including sibling frames' params)
// untouched. This lets multiple DataLists coexist on a single page.
export default class extends Controller {
  static targets = ["header", "filterForm"];

  static values = {
    frame: { type: String, default: "" },
    sortColumn: { type: String, default: "" },
    sortDirection: { type: String, default: "asc" },
    columnParam: { type: String, default: "column" },
    directionParam: { type: String, default: "direction" },
    pageParam: { type: String, default: "page" },
  };

  connect() {
    this.isSorting = false;
    this.unlockTimeout = null;
  }

  disconnect() {
    this.clearUnlockFallback();
  }

  // Sort by the clicked column header
  sort(event) {
    if (this.isSorting) {
      event.preventDefault();
      return;
    }

    const header = event.currentTarget;
    const column = header.dataset.column;
    if (!column) return;

    // First click on a column uses its declared default direction (e.g. "Last
    // Updated" starts desc/newest-first). Clicking the already-sorted column
    // flips the current direction.
    const defaultDirection = header.dataset.defaultDirection === "desc" ? "desc" : "asc";
    const opposite = defaultDirection === "desc" ? "asc" : "desc";
    const direction =
      this.sortColumnValue === column && this.sortDirectionValue === defaultDirection
        ? opposite
        : defaultDirection;

    this.lockSorting();
    this.showLoading(header);

    const url = this.buildBaseUrl();
    url.searchParams.set(this.columnParamValue, column);
    url.searchParams.set(this.directionParamValue, direction);
    this.applyFilterFormParams(url);
    url.searchParams.delete(this.pageParamValue);

    this.navigateTo(url);
  }

  // Re-fetch the frame when any filter input changes
  filterChanged() {
    const url = this.buildBaseUrl();
    if (this.sortColumnValue) url.searchParams.set(this.columnParamValue, this.sortColumnValue);
    if (this.sortDirectionValue) url.searchParams.set(this.directionParamValue, this.sortDirectionValue);
    this.applyFilterFormParams(url);
    url.searchParams.delete(this.pageParamValue);

    this.navigateTo(url);
  }

  filterSubmit(event) {
    event.preventDefault();
    this.filterChanged();
  }

  // Build a URL seeded with the frame's current src (if any), falling back to
  // the page URL. This keeps each frame's params independent — sorting in one
  // frame won't drop another frame's pagination/filter state.
  buildBaseUrl() {
    if (this.frameValue) {
      const frame = document.getElementById(this.frameValue);
      if (frame && frame.src) {
        return new URL(frame.src, window.location.origin);
      }
    }
    return new URL(window.location.href);
  }

  applyFilterFormParams(url) {
    if (!this.hasFilterFormTarget) return;
    const formData = new FormData(this.filterFormTarget);
    const formFieldNames = new Set();
    for (const [key] of formData.entries()) formFieldNames.add(key);
    for (const name of formFieldNames) url.searchParams.delete(name);
    for (const [key, value] of formData.entries()) {
      if (value !== "" && value !== null) url.searchParams.append(key, value);
    }
  }

  navigateTo(url) {
    if (this.frameValue) {
      const frame = document.getElementById(this.frameValue);
      if (frame) {
        this.attachFrameUnlockListeners(frame);
        frame.src = url.toString();
        return;
      }
    }
    window.location.href = url.toString();
  }

  // ── loading state ───────────────────────────────────────────────────────────

  showLoading(header) {
    const indicator = header.querySelector("[data-datatable-target='indicator']");
    const loader = header.querySelector("[data-datatable-target='loader']");
    const sortButton = header.querySelector("button[type='button']");
    if (indicator) indicator.classList.add("hidden");
    if (loader) loader.classList.remove("hidden");
    if (sortButton) sortButton.disabled = true;
  }

  hideAllLoadingStates() {
    this.headerTargets.forEach((header) => {
      const indicator = header.querySelector("[data-datatable-target='indicator']");
      const loader = header.querySelector("[data-datatable-target='loader']");
      const sortButton = header.querySelector("button[type='button']");
      if (indicator) indicator.classList.remove("hidden");
      if (loader) loader.classList.add("hidden");
      if (sortButton) sortButton.disabled = false;
    });
  }

  setHeadersDisabled(disabled) {
    this.headerTargets.forEach((header) => {
      header.classList.toggle("pointer-events-none", disabled);
      header.classList.toggle("opacity-70", disabled);
      header.setAttribute("aria-disabled", disabled ? "true" : "false");
    });
  }

  lockSorting() {
    this.isSorting = true;
    this.setHeadersDisabled(true);
    this.startUnlockFallback();
  }

  unlockSorting() {
    this.isSorting = false;
    this.setHeadersDisabled(false);
    this.hideAllLoadingStates();
    this.clearUnlockFallback();
  }

  startUnlockFallback() {
    this.clearUnlockFallback();
    this.unlockTimeout = setTimeout(() => this.unlockSorting(), 10000);
  }

  clearUnlockFallback() {
    if (this.unlockTimeout) {
      clearTimeout(this.unlockTimeout);
      this.unlockTimeout = null;
    }
  }

  attachFrameUnlockListeners(frame) {
    const unlock = () => {
      frame.removeEventListener("turbo:frame-load", unlock);
      frame.removeEventListener("turbo:fetch-request-error", unlock);
      this.unlockSorting();
    };
    frame.addEventListener("turbo:frame-load", unlock);
    frame.addEventListener("turbo:fetch-request-error", unlock);
  }
}
